import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCountdown, metaLine, cardState } from "@/components/AuctionCard";
import { NativeShare } from "@/components/NativeShare";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge, SplitTitle, money } from "@/components/auction/AuctionUI";
import { Gallery, PriceHead, BidForm, PayWarning, LeadingPanel, OutbidPanel, BlockedPanel, EndedPanel, BidHistory, InfoCard } from "@/components/auction/DetailParts";
import { WinnerPanel } from "@/components/auction/WinnerPanel";
import { useAuctionTerms, TermsCheck, TermsLink } from "@/components/auction/AuctionTerms";
import { Lock, Zap, ArrowLeft } from "lucide-react";
import { HitButton, HitSectionLoading, useAsyncAction, useLoadingLevel } from "@/components/loading";

const POLL_MS = 5000;

function shareText(a) {
  if (a.is_ended) return a.result === "sold" && a.final_price != null
    ? `Subasta vendida en HitTCG: ${a.product_name} — vendida en ${money(a.final_price)}. Mira las subastas en vivo:`
    : `Subasta finalizada en HitTCG: ${a.product_name}. Mira las subastas en vivo:`;
  const has = a.current_bid != null;
  const cierre = a.ends_at ? new Date(a.ends_at).toLocaleString("es-EC", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  return `🔨 SUBASTA en HitTCG: ${a.product_name}. ${has ? `Puja actual ${money(a.current_bid)}` : `Precio de salida ${money(a.starting_price)}`} · cierra ${cierre} · ¡Entra y puja!`;
}

function ConfirmSheet({ a, amount, days, onCancel, onOk, termsAccepted, loading, done }) {
  const [ok, setOk] = useState(false);
  return (
    <AlertDialog open={amount != null} onOpenChange={(o) => !o && !loading && !done && onCancel()}>
      <AlertDialogContent data-testid="bid-confirm-modal"
        className="sx bg-[#101010] border-white/12 rounded-[28px] p-6 gap-0 max-sm:!top-auto max-sm:!bottom-0 max-sm:!left-0 max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!w-full max-sm:!max-w-none max-sm:!rounded-b-none">
        <div className="sm:hidden mx-auto -mt-2 mb-4 w-11 h-1 rounded-full bg-white/25" />
        <AlertDialogTitle className="m-0">
          <span className="sx-mono block text-[10px] tracking-[.14em] text-[#FF4438]">CONFIRMAR PUJA</span>
          <span className="sx-price block text-[52px] text-[#34D399] mt-2">${amount}</span>
        </AlertDialogTitle>
        <div className="text-sm text-white/55 mt-1">{a.product_name}</div>
        <AlertDialogDescription data-testid="bid-confirm-text" className="mt-5 rounded-[18px] border border-[#FF4438]/45 bg-[#FF4438]/[.08] p-4 text-[15px] leading-relaxed text-white/75">
          Vas a pujar <b className="text-white">${amount}</b>. Si ganas, debes pagar <b className="text-white">${amount}</b> dentro de{" "}
          <b className="text-white">{days} día{days > 1 ? "s" : ""}</b> desde el cierre. Si no pagas a tiempo,{" "}
          <b className="text-[#ff6b61]">tu cuenta será baneada</b>.
        </AlertDialogDescription>
        {!termsAccepted && <div className="mt-4"><TermsCheck checked={ok} onChange={setOk} testid="bid-terms-check" /></div>}
        <div className="mt-5 space-y-2.5">
          <HitButton data-testid="bid-confirm-ok" disabled={!termsAccepted && !ok} onClick={onOk} loading={loading} done={done} tone="dark"
                     loadingText="Confirmando…" doneText="Confirmada"
                     className="sx-btn w-full h-[54px] text-base hover:bg-[#ff5a4f] disabled:opacity-40 inline-flex items-center justify-center gap-2.5">Confirmar puja</HitButton>
          <AlertDialogCancel data-testid="bid-confirm-cancel" disabled={loading || done} className="sx-btn-ghost w-full h-[52px] m-0 !bg-transparent !text-white !border-white/20 hover:!bg-white/5">Cancelar</AlertDialogCancel>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function StickyBar({ a, onBid }) {
  const c = useCountdown(a.ends_at);
  const has = a.current_bid != null;
  return (
    <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 sx-rise bg-[#080808]/95 backdrop-blur-xl border-t border-white/10 px-5 py-3.5 flex items-center justify-between gap-3"
         style={{ paddingBottom: "calc(14px + env(safe-area-inset-bottom))" }} data-testid="auction-sticky-bar">
      <div>
        <div className="sx-price text-[24px]" style={{ color: has ? "#34D399" : "#fff" }}>{money(has ? a.current_bid : a.starting_price)}</div>
        <div className="sx-mono text-xs text-[#FF4438] mt-1">{c}</div>
      </div>
      <button type="button" onClick={onBid} data-testid="sticky-bid-btn" className="sx-btn h-[52px] px-7 text-base">Pujar ${a.min_next_bid}</button>
    </div>
  );
}

function useOutOfView(ref, enabled) {
  const [out, setOut] = useState(false);
  useEffect(() => {
    if (!enabled || !ref.current) { setOut(false); return; }
    const io = new IntersectionObserver(([e]) => setOut(!e.isIntersecting && e.boundingClientRect.top < 0));
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, enabled]);
  return out;
}

function BidArea({ a, amount, setAmount, ask, busy, done }) {
  const days = a.payment_deadline_days || 1;
  const submit = (e) => {
    e.preventDefault();
    const n = Number(amount);
    if (!Number.isInteger(n)) { toast.error("Solo pujas enteras, sin centavos"); return; }
    if (n < a.min_next_bid) { toast.error(`La puja mínima es $${a.min_next_bid}`); return; }
    ask(n);
  };
  const mine = (a.bids || []).filter((b) => b.is_you).map((b) => b.amount);
  const myBest = mine.length ? Math.max(...mine) : null;
  if (a.you_are_leading && a.bid_block_reason) {
    return <div className="space-y-3"><LeadingPanel a={a} /><BlockedPanel a={a} leading /></div>;
  }
  if (!a.bid_block_reason && myBest != null) {
    return <div className="space-y-3"><OutbidPanel a={a} myBest={myBest} amount={amount} setAmount={setAmount} onSubmit={submit} busy={busy} done={done} /><PayWarning days={days} /></div>;
  }
  return (
    <div className="space-y-3">
      <div className="sx-card p-4 space-y-4" data-testid="auction-price-panel">
        <PriceHead a={a} />
        {!a.bid_block_reason && <BidForm a={a} amount={amount} setAmount={setAmount} onSubmit={submit} busy={busy} done={done} />}
      </div>
      {a.bid_block_reason ? <BlockedPanel a={a} /> : <PayWarning days={days} />}
    </div>
  );
}

export default function SubastaDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState("");
  const [confirm, setConfirm] = useState(null);
  const panelRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/auctions/${id}`)
      .then((r) => { setA(r.data); setError(null); })
      .catch((err) => setError(err?.response?.status === 410 ? "ended" : "notfound"));
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load, user?.id]);

  const minBid = a?.min_next_bid;
  useEffect(() => { if (minBid != null) setAmount(String(minBid)); }, [minBid]);
  const canBid = !!a && !a.is_ended && !a.bid_block_reason;
  const terms = useAuctionTerms();
  const showBar = useOutOfView(panelRef, canBid);
  const level = useLoadingLevel(!a && !error);

  const bid = useAsyncAction(async () => {
    const n = confirm;
    try {
      if (!terms.accepted) await terms.accept("bid");
      const r = await api.post(`/auctions/${a.id}/bid`, { amount: n });
      return () => {
        setConfirm(null);
        toast.success(r.data.extended ? `¡Puja de $${n} registrada! El cierre se extendió 2 minutos.` : `¡Puja de $${n} registrada!`);
        load();
      };
    } catch (err) {
      setConfirm(null);
      toast.error(err?.response?.data?.detail || "No se pudo registrar la puja");
      load();
      return false;
    }
  });

  if (error) return (
    <div className="sx max-w-xl mx-auto px-5 py-16 text-center" data-testid="auction-detail-error">
      <Lock className="mx-auto text-white/30 mb-3" size={32} />
      <p className="text-white/70 mb-5">{error === "ended" ? "Esta subasta ya finalizó." : "Subasta no encontrada."}</p>
      <Link to="/subastas" className="sx-btn inline-flex h-[52px] px-8 items-center">Ver subastas</Link>
    </div>
  );
  if (!a || level !== "none") return <div className="sx max-w-xl mx-auto px-5 py-8 min-h-[70vh]" data-testid="auction-detail-loading"><HitSectionLoading level={level} loading={!a} section="auction" onRetry={load} /></div>;

  const st = cardState(a);
  const badges = (
    <span className="flex gap-2">
      <Badge tone={st.badge[0]} testid="auction-detail-status">{st.badge[1]}</Badge>
      {a.duration_days === 1 && !a.is_ended && <Badge tone="turbo"><Zap size={10} fill="currentColor" /> Turbo</Badge>}
    </span>
  );
  const offerPanel = a.my_offer && ["awaiting_payment", "repechage_offered"].includes(a.result);

  return (
    <div className="sx relative overflow-hidden" data-testid="auction-detail">
      <div className="sx-halo" />
      <div className="relative max-w-[1280px] mx-auto px-5 lg:px-10 pt-5 pb-28 lg:pb-14">
        <div className="flex items-center justify-between mb-4 lg:mb-6">
          <Link to="/subastas" className="inline-flex items-center gap-1.5 text-sm text-white/65 hover:text-white" data-testid="auction-detail-back">
            <ArrowLeft size={14} /> <span className="lg:hidden">Subastas</span><span className="hidden lg:inline">Volver a subastas</span>
          </Link>
          <span className="lg:hidden">{badges}</span>
          <TermsLink testid="detail-terms-link" className="hidden lg:inline-flex" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-5 lg:gap-x-10 lg:gap-y-6 items-start">
          <div className="lg:col-start-1 lg:row-start-1"><Gallery photos={a.photos || []} alt={a.product_name} glow={st.tone} /></div>
          <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-24 space-y-4">
            <div className="hidden lg:block">{badges}</div>
            <div>
              <SplitTitle text={a.product_name} testid="auction-detail-title" className="text-[30px] lg:text-[52px]" />
              <div className="text-sm text-white/55 mt-2">{metaLine(a, false)}</div>
            </div>
            <div ref={panelRef}>
              {offerPanel ? <WinnerPanel a={a} onDone={load} />
                : a.is_ended ? <EndedPanel a={a} />
                : <BidArea a={a} amount={amount} setAmount={setAmount} ask={setConfirm} busy={bid.loading} done={bid.done} />}
            </div>
          </div>
          <div className="lg:col-start-1 lg:row-start-2 space-y-4">
            {!a.is_ended && <p className="text-xs text-white/50">Pujas en los últimos 2 minutos extienden el cierre 2 minutos.</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <div className="sx-card p-4" data-testid="auction-share-card">
                <div className="sx-mono text-[10px] tracking-[.14em] text-[#FF4438]">COMPARTIR</div>
                <p className="text-sm text-white/70 mt-1 mb-3">Más postores, mejor precio final.</p>
                {a.short_code && (
                  <div className="mb-3 font-mono text-[13px] text-white/80 truncate" data-testid="auction-short-link">
                    {`${API_BASE}/s/${a.short_code}`.replace(/^https?:\/\//, "")}
                  </div>
                )}
                <NativeShare tiles title={`Subasta: ${a.product_name}`} text={shareText(a)} url={a.short_code ? `${API_BASE}/s/${a.short_code}` : `${API_BASE}/share/subastas/${a.id}`} imageUrl={`${API_BASE}/share/subastas/${a.id}/og.jpg?v=${a.current_bid ?? a.starting_price}-${a.bid_count ?? 0}-${a.result || a.status}`} testid="auction-share" />
              </div>
              <InfoCard a={a} />
            </div>
            {!a.public_result_view && <BidHistory bids={a.bids || []} />}
          </div>
        </div>
      </div>
      {canBid && showBar && <StickyBar a={a} onBid={() => setConfirm(a.min_next_bid)} />}
      <ConfirmSheet key={confirm ?? "x"} a={a} amount={confirm} days={a.payment_deadline_days || 1} termsAccepted={terms.accepted} onCancel={() => setConfirm(null)} onOk={bid.run} loading={bid.loading} done={bid.done} />
    </div>
  );
}
